<template>
  <div class="min-h-screen bg-primary w-full h-full mt-[60px] lg:mt-0 themeTransition">
    <div id="wrapper" v-if="!isLoading">
      <page-wrapper v-if="pageGUI" :page-gui="pageGUI">
        <!-- content begin -->
        <section class="sectionContainer mb-5 lg:min-h-full lg:mb-9" ref="scrollElement">
          <article
            class="col-start-2 col-span-12 mt-16 md:col-span-8 md:col-start-2 md:grid md:grid-cols-8 md:gap-x-12 md:mt-24 2xl:gap-x-20"
          >
            <paragraph
              v-if="subHeading"
              classes="md:col-span-6 font-bold mb-4 md:mb-8"
              :is-umbraco="isUmbraco"
              :theme="themeType"
              :text="subHeading"
            />
            <header1
              v-if="title"
              :text="title"
              classes="mb-10 md:col-span-8 lg:col-span-6 md:mb-12"
              :is-umbraco="isUmbraco"
              :theme="themeType"
            />
            <rich-text
              v-if="description"
              :text="description"
              :isUmbraco="isUmbraco"
              :theme="themeType"
              :isManchet="true"
              classes="md:col-span-6 mb-10 md:mb-12"
            />
          </article>
        </section>
        <section class="sectionContainer mb-5 lg:mb-9 min-h-min">
          <div
            id="cookiecontainer"
            class="col-start-2 col-span-12 md:col-span-6 md:col-start-5"
          ></div>
          <div id="content" class="col-span-full">
            <component
              v-for="(section, index) in sections"
              :key="index"
              :is="getComponent(section?.name as ComponentType)"
              :ref="section?.name"
              v-bind="{ ...section?.props }"
            ></component>
          </div>
        </section>
        <!-- content end -->
      </page-wrapper>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, computed, onMounted, onUpdated } from "vue";
import { parseUrl, changeTheme, getThemeType } from "@/util/helper";
import Header1 from "@components/Typography/Header1.ce.vue";

import RichText from "@components/Typography/RichText.ce.vue";
import PageWrapper from "@components/PageWrapper.vue";
import Paragraph from "@components/Typography/Paragraph.ce.vue";
import { Theme } from "@/types/types";
import { getPage, getPageByID } from "@/util/api";
import { ICookieinformationPage } from "@/types/ICookieinformationPage";
import { getComponentData, getComponent } from "@/util/getComponent";
import { ComponentType } from "@/util/constants";

const isLoading = ref(true);
const environment = ref();
const urlParams = ref<String[] | null>(null);

const pageData = ref<ICookieinformationPage | null>(null);
const pageGUI = ref<string | undefined>();

const theme = ref<Theme | null>(null);

const iconArray = ref<Array<{ icon: string }>>([]);
const currentUrl = ref<string | null>(null);
const shareLabel = ref<string>("");

const isPreview = ref<boolean>(false);
const previewUrl = ref<string>("");
const isUmbraco = ref(false);
const pageKey = ref<string | undefined>();

const sections = computed(() => {
  if (!pageData.value?.properties.grid) return [];
  const sections = pageData.value?.properties.grid.items.map((item) => {
    const { content } = item;
    if (!content) return;
    return getComponentData(content, theme.value);
  });
  const sanitizedSections = sections.filter(Boolean);
  return sanitizedSections;
});

onBeforeMount(async () => {
  const metaUrl = document.head.querySelector("meta[property='og:url']");
  if (metaUrl instanceof HTMLMetaElement) {
    previewUrl.value = metaUrl.content.replace(/^https?:\/\/[^/]+/, "");
  }
  environment.value = parseUrl(window.location.href).environment;
  urlParams.value = parseUrl(window.location.href).params;

  //create social share icons
  currentUrl.value = window.location.href;

  if (!urlParams.value) return;

  let path = urlParams.value.join("/");

  if (previewUrl.value.indexOf(path) < 0) {
    path = previewUrl.value;
    isPreview.value = true;
  }
  if (path) {
    pageKey.value = document.documentElement.dataset.id;
    pageData.value = (await getPageByID(
      pageKey.value,
      isPreview.value
    )) as ICookieinformationPage;
    //pageData.value = (await getPage(path, isPreview.value)) as ICookieinformationPage;
    if (pageData.value) {
      theme.value = pageData.value.properties.backgroundColor;
      if (theme.value) changeTheme(theme.value);
      pageGUI.value = pageData.value.route.startItem.id;
      isLoading.value = false;
    }
  }
});

onMounted(() => {
  let fondEnvironment = environment.value.toLowerCase().includes("fond");
  let cookieID = fondEnvironment
    ? "a8d71105-5f9f-40c8-962a-654727c84cd4"
    : "0f8a53ec-9cd5-47f9-8c3c-2b286c927fb4";
  const script = document.createElement("script");
  script.id = "CookieDeclaration";
  script.src = `https://consent.cookiebot.com/${cookieID}/cd.js`;
  script.type = "text/javascript";
  script.async = true;

  const content = document.getElementById("#content");
  content?.parentNode?.insertBefore(script, content);
});

const pageProperties = computed(() => {
  return pageData.value?.properties;
});

onUpdated(() => {
  let fondEnvironment = environment.value.toLowerCase().includes("fond");
  let cookieID = fondEnvironment
    ? "a8d71105-5f9f-40c8-962a-654727c84cd4"
    : "0f8a53ec-9cd5-47f9-8c3c-2b286c927fb4";
  const script = document.createElement("script");
  script.id = "CookieDeclaration";
  script.src = `https://consent.cookiebot.com/${cookieID}/cd.js`;
  script.type = "text/javascript";
  script.async = true;

  const content = document.getElementById("cookiecontainer");
  content?.insertAdjacentElement("beforeend", script);
});

const themeType = computed(() => {
  if (!theme.value) return "dark";
  return getThemeType(theme.value);
});

const title = computed(() => {
  return pageProperties.value?.title ?? "";
});

const description = computed(() => {
  return pageProperties.value?.description?.markup ?? "";
});

const subHeading = computed(() => {
  return pageProperties.value?.tag ?? "";
});
</script>
